import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl, uploadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, ImagePlus, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Advertise() {
  const { user } = useAuth();
  const [packages, setPackages] = useState({});
  const [paypal, setPaypal] = useState("");
  const [selected, setSelected] = useState("basic");
  const [form, setForm] = useState({ title: "", target_url: "", position: "home_top", sponsor_name: "", size: "medium", image_url: "", payer_note: "" });
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get("/ads/packages").then(({ data }) => { setPackages(data.packages); setPaypal(data.paypal_link); });
  }, []);

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setForm((f) => ({ ...f, image_url: url }));
      toast.success("Banner uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Add an ad title");
    try {
      await api.post("/ads/purchase", { ...form, package: selected });
      setSubmitted(true);
      toast.success("Ad submitted for approval");
    } catch (e) {
      toast.error("Could not submit");
    }
  };

  const pkg = packages[selected] || {};

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-8 py-10">
      <p className="font-tech text-xs uppercase tracking-widest brand-text">Sponsorship</p>
      <h1 className="font-display text-4xl font-bold mb-2">Advertise with Drops of Honey</h1>
      <p className="text-muted-page mb-8 max-w-2xl">Put your shop, auction, or club in front of dedicated insulator collectors. Pick a package, pay securely with PayPal, and submit your banner — we'll review and publish it.</p>

      <div className="grid gap-4 sm:grid-cols-3 mb-10">
        {Object.entries(packages).map(([key, p]) => (
          <button key={key} data-testid={`ad-package-${key}`} onClick={() => setSelected(key)} className={`text-left surface border rounded-2xl p-5 transition-all ${selected === key ? "ring-2 ring-amber-500 -translate-y-1 shadow-lg" : "hover:shadow"}`}>
            <div className="flex items-center justify-between">
              <span className="font-display text-xl font-bold">{p.name}</span>
              {selected === key && <Check className="w-5 h-5 brand-text" />}
            </div>
            <p className="font-tech text-3xl font-bold mt-2 brand-text">${p.price}</p>
            <p className="text-sm text-muted-page mt-1">{p.days} days placement</p>
          </button>
        ))}
      </div>

      {!user ? (
        <div className="surface border rounded-2xl p-8 text-center">
          <p className="font-display text-2xl">Sign in to purchase an ad.</p>
          <Link to="/login" className="inline-block mt-4 px-5 py-2.5 rounded-full font-semibold" style={{ background: "var(--brand)", color: "#fff" }}>Sign in</Link>
        </div>
      ) : submitted ? (
        <div className="surface border rounded-2xl p-8 text-center" data-testid="ad-submitted">
          <div className="w-14 h-14 rounded-full grid place-items-center mx-auto mb-3" style={{ background: "var(--honey-100)" }}><Check className="w-7 h-7 brand-text" /></div>
          <h2 className="font-display text-2xl font-bold">Ad submitted!</h2>
          <p className="text-muted-page mt-1">Complete payment via PayPal below (if you haven't). We'll review and publish your ad shortly.</p>
          <a href={paypal} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 px-6 py-3 rounded-full font-semibold text-white" style={{ background: "#0070ba" }}>Pay ${pkg.price} via PayPal <ExternalLink className="w-4 h-4" /></a>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="surface border rounded-2xl p-6 space-y-4" data-testid="ad-purchase-form">
            <h2 className="font-display text-2xl font-bold">Your ad</h2>
            <div>
              <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Ad title</label>
              <input data-testid="ad-title-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
            </div>
            <div>
              <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Sponsor / business name</label>
              <input data-testid="ad-sponsor-input" value={form.sponsor_name} onChange={(e) => setForm((f) => ({ ...f, sponsor_name: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5" />
            </div>
            <div>
              <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Target link</label>
              <input data-testid="ad-target-input" value={form.target_url} onChange={(e) => setForm((f) => ({ ...f, target_url: e.target.value }))} placeholder="https://…" className="w-full surface border rounded-lg px-3 py-2.5" />
            </div>
            <div>
              <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Placement</label>
              <select data-testid="ad-position-select" value={form.position} onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))} className="w-full surface border rounded-lg px-3 py-2.5">
                <option value="home_top">Homepage banner</option>
                <option value="home_side">Homepage side rail</option>
                <option value="catalog">Catalog page</option>
                <option value="login">Login page</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-tech uppercase tracking-widest text-muted-page mb-1">Ad size</label>
              <div className="grid grid-cols-3 gap-2" data-testid="ad-size-select">
                {["small", "medium", "large"].map((s) => (
                  <button key={s} type="button" onClick={() => setForm((f) => ({ ...f, size: s }))} className={`px-3 py-2 rounded-lg text-sm font-semibold border capitalize ${form.size === s ? "text-white" : "surface"}`} style={form.size === s ? { background: "var(--brand)" } : {}}>{s}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-full surface border text-sm font-semibold">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />} Upload banner image
                <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="ad-image-input" />
              </label>
            </div>
          </div>

          <div className="surface border rounded-2xl p-6 flex flex-col">
            <h2 className="font-display text-2xl font-bold mb-3">Preview</h2>
            <div className="surface border rounded-xl overflow-hidden mb-4">
              {form.image_url && <img src={mediaUrl(form.image_url)} alt="" className="w-full h-32 object-cover" />}
              <div className="p-3">
                <p className="font-display text-lg font-semibold">{form.title || "Your ad title"}</p>
                {form.sponsor_name && <p className="text-xs text-muted-page">Sponsored by {form.sponsor_name}</p>}
              </div>
            </div>
            <div className="mt-auto">
              <div className="flex items-center justify-between mb-3">
                <span className="text-muted-page">{pkg.name}</span>
                <span className="font-tech text-2xl font-bold brand-text">${pkg.price}</span>
              </div>
              <button data-testid="submit-ad-btn" onClick={submit} className="w-full py-3 rounded-full font-semibold mb-2" style={{ background: "var(--brand)", color: "#fff" }}>Submit ad for approval</button>
              <a href={paypal} target="_blank" rel="noopener noreferrer" data-testid="paypal-pay-btn" className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full font-semibold text-white" style={{ background: "#0070ba" }}>Pay via PayPal <ExternalLink className="w-4 h-4" /></a>
              <p className="text-xs text-muted-page mt-2 text-center">After payment, your ad is reviewed by an admin before going live.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
